'use client'

import { Button } from '@ethena/shared-ui/src/components/Button'
import { EthenaTooltip } from '@ethena/shared-ui/src/components/EthenaTooltip'
import { GradientDivider } from '@ethena/shared-ui/src/components/GradientDivider'
import { InfoIcon } from '@ethena/shared-ui/src/components/InfoIcon'
import { NavBar, Route } from '@ethena/shared-ui/src/components/NavBar/NavBar'
import { Pill } from '@ethena/shared-ui/src/components/Pill'
import Placeholder from '@ethena/shared-ui/src/components/Placeholder'
import { Stats } from '@ethena/shared-ui/src/components/Stats'
import { StatTooltip } from '@ethena/shared-ui/src/components/StatTooltip'
import { TvlBreakdownTooltipContent } from '@ethena/shared-ui/src/components/TvlBreakdownTooltipContent'
import { DAPP_URL } from '@ethena/shared-ui/src/constants/url'
import { useSusdeYieldTooltipData } from '@ethena/shared-ui/src/hooks/useSusdeYieldTooltipData'
import { cn } from '@ethena/shared-ui/utils'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { useEffect, useState } from 'react'

import { IS_GERMAN } from '@/constants'
import { useElementInView } from '@/hooks/useElementInView'
import { useTvl } from '@/hooks/useTvl'
import useUserStore from '@/store/user-store'

const NavBarRight = () => {
  const { tvl, usdeTvlData, usdtbTvlData } = useTvl()
  const { tooltip, yieldApy, yieldStr } = useSusdeYieldTooltipData()

  return (
    <div className="sm:flex hidden gap-2">
      {IS_GERMAN ? null : (
        <Pill singleBorder>
          <div className="flex items-center justify-center gap-2.5">
            <div className="flex items-center justify-center gap-1">
              <Image src="/shared/usde.svg" alt="USDe icon" height={12} width={12} className="h-3 w-3" />
              <Placeholder isLoading={!tvl} classes="w-[50px] h-4">
                {tvl}
              </Placeholder>
              <EthenaTooltip
                content={<TvlBreakdownTooltipContent usdeTvlData={usdeTvlData} usdtbTvlData={usdtbTvlData} />}
                width="w-[200px] sm:w-[250px]"
              >
                <InfoIcon className="w-3 h-3" />
              </EthenaTooltip>
            </div>
            <GradientDivider height="h-[20px]" />
            <div className="flex items-center justify-center gap-1">
              <Image src="/shared/sUSDe.svg" alt="sUSDe icon" height={12} width={12} className="h-3 w-3" />
              <Placeholder isLoading={!yieldStr} classes="w-[25px] h-4">
                {yieldStr} <span>APY</span>
              </Placeholder>
              <EthenaTooltip
                content={<StatTooltip title={`sUSDe APY: ${yieldApy ?? '-'}`} description={tooltip} />}
                width="w-[200px] sm:w-[250px]"
              >
                <InfoIcon className="w-3 h-3" />
              </EthenaTooltip>
            </div>
          </div>
        </Pill>
      )}
      {IS_GERMAN ? null : (
        <Button asChild variant="secondary" size="small">
          <Link href={DAPP_URL} target="_blank" rel="noreferrer">
            Launch App
          </Link>
        </Button>
      )}
    </div>
  )
}

const NavBarBottomMobile = () => {
  const { isIdle } = useUserStore()
  return IS_GERMAN ? null : <Stats isIdle={isIdle} isMobileMenuStats />
}

export function LandingWrapper({ children, routes }: { children: React.ReactNode; routes: Route[] }) {
  // Get the current pathname
  const pathname = usePathname()

  const isHeroInView = useElementInView('hero-section', {
    rootMargin: '-40% 0px 0px 0px',
    threshold: 0.5,
  })

  const [dynamicIsHomePage, setDynamicIsHomePage] = useState(false)

  // Immediately determine if we're on the homepage - this happens during render
  const isHomePage = pathname === '/'

  // Log pathname changes for debugging
  useEffect(() => {
    setDynamicIsHomePage(pathname === '/')
  }, [pathname])

  return (
    <div className="flex flex-col min-h-screen">
      <div
        className={cn(
          'w-full fixed flex justify-center z-50 transition-[top] duration-300 ease-in-out nav-section',
          (!isHomePage && !dynamicIsHomePage) || !isHeroInView ? 'top-5' : 'top-5 lg:top-12 lg:hlg:top-14',
          IS_GERMAN && isHomePage && 'absolute',
        )}
      >
        <div className="w-full landing-layout">
          <NavBar
            routes={routes.filter(route => route.title !== 'Community')}
            right={<NavBarRight />}
            bottomMobile={<NavBarBottomMobile />}
            className="mt-0 mx-0 font-normal bg-[#09090B]/90"
          />
        </div>
      </div>
      {children}
    </div>
  )
}
